import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { SupTransactionType, SupWeb3DepositStep, SupWeb3TxSingleVault } from "@/types";
import React, { type Dispatch, type SetStateAction, useMemo } from "react";

import SupLoading from "@/components/Ui/Loading";
import Web3CommonRouteDefault from "@/components/Web3/Common/Route/Default";
import Web3CommonRouteDirect from "@/components/Web3/Common/Route/Direct";
import { useRouteState } from "@/components/Web3/Common/Route/State";
import { getWeb3TxRoutes } from "@/components/Web3/Common/Route/utils/getWeb3TxRoutes";
import prepareProviderRoutes from "@/components/Web3/Common/Route/utils/prepareProviderRoutes";
import { useStoredDataDispatch } from "@/store/Context";
import { SupAccordion, SupAccordionDetails, SupAccordionSummary, SupText } from "@repo/core-ui";
import { twMerge } from "tailwind-merge";

interface Web3CommonVaultRouteContainerProps {
  singleVault: SupWeb3TxSingleVault;
  setStep: Dispatch<SetStateAction<SupWeb3DepositStep>>;
  type?: SupTransactionType;
  calculatedRoutes: Web3TxMultiCalcSWR;
}

export default function Web3CommonVaultRouteContainer({ singleVault, setStep, type = "deposit", calculatedRoutes }: Web3CommonVaultRouteContainerProps) {
  const { getVaultSelectedRoute } = useRouteState();

  const selectedRoute = useMemo(() => getVaultSelectedRoute?.(singleVault.id)?.selectedRoute, [getVaultSelectedRoute, singleVault]);

  const storedDispatch = useStoredDataDispatch();

  const { isLoading } = calculatedRoutes;

  const { calculatedRoute, isError } = useMemo(() => getWeb3TxRoutes(singleVault.id, calculatedRoutes), [calculatedRoutes, singleVault.id]);

  const preparedRoutes = useMemo(() => {
    const result = prepareProviderRoutes(calculatedRoute, singleVault, type);
    return result || [];
  }, [calculatedRoute, singleVault, type]);

  const isReady = useMemo(() => {
    if (isLoading) {
      return false;
    }
    if (isError) {
      return false;
    }
    return preparedRoutes.length > 0 || calculatedRoute?.in.is_same_route;
  }, [isLoading, isError, preparedRoutes.length, calculatedRoute?.in.is_same_route]);

  const { primaryRoutes, otherRoutes } = useMemo(() => {
    return {
      primaryRoutes: preparedRoutes.slice(0, 1),
      otherRoutes: preparedRoutes.slice(1),
    };
  }, [preparedRoutes]);

  return (
    <div className={twMerge("flex flex-col items-stretch space-y-1", isReady && !otherRoutes.length && calculatedRoute && "mb-6")}>
      {isReady && (preparedRoutes.length > 0 || calculatedRoute?.in.is_same_route) && (
        <SupText variant="caption" className="mb-1 mt-6 w-full">
          {type === "deposit" ? "receiving" : "withdrawing"}
        </SupText>
      )}

      {/*No routes*/}
      {isReady && calculatedRoute?.in.is_same_route && <Web3CommonRouteDirect singleVault={singleVault} calculatedRoute={calculatedRoute} type={type} />}

      {/*Primary routes*/}
      {isReady &&
        calculatedRoute &&
        !calculatedRoute.in.is_same_route &&
        primaryRoutes.map(singleRoute => (
          <Web3CommonRouteDefault route={singleRoute} selectedRoute={selectedRoute} key={`vault-priority-route-${singleRoute.vault_id}-${singleRoute.id}`} />
        ))}

      {/*Additional routes*/}
      {isReady && !!otherRoutes.length && calculatedRoute && (
        <SupAccordion>
          <SupAccordionSummary className="my-6 w-full">
            <SupText variant="caption" className="w-full">
              ADDITIONAL ROUTES ({otherRoutes.length})
            </SupText>
          </SupAccordionSummary>
          <SupAccordionDetails>
            <div className="flex w-full flex-col items-stretch space-y-1">
              {otherRoutes.map(singleRoute => (
                <Web3CommonRouteDefault route={singleRoute} selectedRoute={selectedRoute} key={`vault-${singleRoute.vault_id}-${singleRoute.id}`} />
              ))}
            </div>
          </SupAccordionDetails>
        </SupAccordion>
      )}

      {isLoading && (
        <div className="flex w-full flex-col items-center justify-center py-5">
          <SupLoading />
          <SupText variant="detail" className="mt-4">
            Calculating the best Route for you...BRB!
          </SupText>
        </div>
      )}

      {isError && (
        <div className="flex w-full space-x-[2px] bg-[transparent] py-6">
          <button
            className="btn-neutral-300 max-h-[24px] min-w-[50px] border bg-neutral p-[8px] text-[10px] text-neutral-900 hover:bg-neutral-600 hover:text-neutral"
            onClick={() => storedDispatch({ type: "toggleCart", vault: singleVault })}
          >
            Remove
          </button>
          <button
            className="btn-neutral-300 max-h-[24px] min-w-[50px] border bg-neutral p-[8px] text-[10px] text-neutral-900 hover:bg-neutral-600 hover:text-neutral"
            onClick={() => setStep("Balance")}
          >
            Reselect&nbsp;asset
          </button>
        </div>
      )}
    </div>
  );
}
